import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Badge, Button, Spinner, Modal, Form } from "react-bootstrap";
import { IoClose } from "react-icons/io5";
import { IoMdRefresh } from "react-icons/io";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaGlobeAmericas,
  FaGripVertical,
  FaRegStar,
} from "react-icons/fa";
import { MdAdd, MdSearch, MdViewKanban, MdErrorOutline, MdCheckCircle } from "react-icons/md";
import axios from "axios";

import NavMenu from "../components/NavMenu";
import Avatar from "../components/Avatar";
import { useTheme } from "../components/ThemeContext";
import "./kanbanPage.css";

const API = process.env.REACT_APP_API || "";

const safeText = (v) => (v == null ? "" : String(v));

// Fixed-order identity palette (shared with the Dashboard stat cards) -
// each priority column keeps a consistent accent as columns are added/removed.
const COLUMN_ACCENTS = ["accent-blue", "accent-violet", "accent-teal", "accent-green", "accent-magenta"];
const accentForIndex = (i) => COLUMN_ACCENTS[i % COLUMN_ACCENTS.length];

const UNASSIGNED = "__ENQUIRES__";

function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return "-";
  const date = new Date(dateTimeStr);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} (${hh}:${min})`;
}

const sourceBadgeVariant = (source = "") => {
  switch (source.toLowerCase()) {
    case "webenquiry":
      return "warning";
    case "phone call":
      return "success";
    case "walk-in":
      return "danger";
    case "whatsapp":
      return "success";
    case "facebook":
      return "primary";
    default:
      return "secondary";
  }
};

// Defined at module scope (not inside EnquiryPriorityKanban) so it keeps a
// stable component identity across re-renders. Previously it was declared
// inline in the parent's render body - every setState during a drag (e.g.
// onDragStart) redefined it, which unmounted/remounted the whole column
// subtree mid-drag and silently killed the browser's native drag session,
// so the first drop attempt did nothing and only a second try worked.
const Column = ({
  title,
  colKey,
  items,
  accentClass,
  deletable,
  onDelete,
  saving,
  dragId,
  setDragId,
  onDropTo,
  onMove,
  moveOptions,
  filtering,
}) => {
  const [isOver, setIsOver] = useState(false);

  return (
    <section
      className={`kbp-col ${accentClass} ${isOver ? "is-over" : ""}`}
      aria-label={`${title}, ${items.length} enquiries`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!isOver) setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={() => {
        setIsOver(false);
        onDropTo(colKey);
      }}
    >
      <header className="kbp-col-head">
        <h2 className="kbp-col-title">
          <span className="kbp-col-dot" />
          <span className="kbp-col-name" title={title}>{title}</span>
        </h2>

        <div className="kbp-col-tools">
          <span className="kbp-col-count">{items.length}</span>

          {deletable && (
            <button
              type="button"
              className="kbp-icon-btn"
              title={`Delete ${title} column`}
              aria-label={`Delete ${title} column`}
              onClick={onDelete}
              disabled={saving}
            >
              <IoClose />
            </button>
          )}
        </div>
      </header>

      <div className="kbp-col-body">
        {items.length === 0 ? (
          <div className="kbp-empty">
            <FaGripVertical className="kbp-empty-icon" aria-hidden="true" />
            {filtering ? "No matches here" : "Drag an enquiry here"}
          </div>
        ) : (
          items.map((it) => {
            const name = (it.name || "Unknown").trim();
            return (
              <article
                key={it.id}
                className={`kbp-card ${dragId === it.id ? "is-dragging" : ""}`}
                draggable={!saving}
                onDragStart={() => setDragId(it.id)}
                onDragEnd={() => setDragId(null)}
              >
                <div className="kbp-card-top">
                  <Avatar name={name} size={38} />

                  <div className="kbp-card-who">
                    <div className="kbp-name" title={name}>{name}</div>
                    <div className="kbp-meta">{formatDateTime(it.submission_date)}</div>
                  </div>

                  <FaGripVertical className="kbp-drag-handle" title="Drag to move" aria-hidden="true" />
                </div>

                <div className="kbp-card-info">
                  <div className="kbp-info-row">
                    <FaEnvelope className="kbp-info-icon" aria-hidden="true" />
                    <span title={it.email || undefined}>{it.email || "-"}</span>
                  </div>
                  <div className="kbp-info-row">
                    <FaPhoneAlt className="kbp-info-icon" aria-hidden="true" />
                    <span>{it.phone_number || "-"}</span>
                  </div>
                  <div className="kbp-info-row">
                    <FaGlobeAmericas className="kbp-info-icon" aria-hidden="true" />
                    <span>{it.country || "-"}</span>
                  </div>
                </div>

                <div className="kbp-card-foot">
                  <Badge bg={sourceBadgeVariant(it.source)} className="kbp-chip">
                    {it.source || "Unknown"}
                  </Badge>
                  <Badge bg="primary" className="kbp-chip">
                    {it.status || "Enquiry"}
                  </Badge>

                  {/* Keyboard / touch alternative to dragging */}
                  <select
                    className="kbp-move"
                    value={colKey}
                    onChange={(e) => onMove(it.id, e.target.value)}
                    disabled={saving}
                    aria-label={`Move ${name} to another column`}
                  >
                    {moveOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.key === colKey ? `In: ${o.title}` : `Move to ${o.title}`}
                      </option>
                    ))}
                  </select>
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
};

export default function EnquiryPriorityKanban() {
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [enquiries, setEnquiries] = useState([]);
  const [priorityRows, setPriorityRows] = useState([]);

  const [dragId, setDragId] = useState(null);
  const [query, setQuery] = useState("");

  const [boardErr, setBoardErr] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [form, setForm] = useState({ priority: "", description: "" });

  const [showDelete, setShowDelete] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [notice, setNotice] = useState("");

  const fetchEnquiries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/getEnquires`);
      if (res.data?.success) setEnquiries(res.data?.data || []);
      else setEnquiries([]);
    } catch (e) {
      console.error(e);
      setEnquiries([]);
    }
  }, []);

  const fetchPriority = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/getPriority`);
      if (res.data?.success) setPriorityRows(res.data?.data || []);
      else setPriorityRows([]);
    } catch (e) {
      console.error(e);
      setPriorityRows([]);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setBoardErr("");
    try {
      await Promise.all([fetchEnquiries(), fetchPriority()]);
    } catch (e) {
      setBoardErr("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [fetchEnquiries, fetchPriority]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const starredEnquiries = useMemo(() => {
    return (enquiries || []).filter((x) => x.starred === 1);
  }, [enquiries]);

  const { priorityCols, priorityById } = useMemo(() => {
    const byId = {};
    (priorityRows || []).forEach((r) => {
      const id = Number(r.priority_id ?? r.id);
      const name = safeText(r.priority).trim();
      if (!id || !name) return;

      if (!byId[id]) {
        byId[id] = {
          priority_id: id,
          priority: name,
          description: r.description,
          created_at: r.created_at || r.createdAt || r.created_date || r.createdDate || null,
        };
      }
    });

    const toTime = (v) => {
      if (!v) return NaN;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? NaN : t;
    };

    const cols = Object.values(byId).sort((a, b) => {
      const ta = toTime(a.created_at);
      const tb = toTime(b.created_at);

      if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;

      if (Number.isNaN(ta) && !Number.isNaN(tb)) return -1;
      if (!Number.isNaN(ta) && Number.isNaN(tb)) return 1;

      return (a.priority_id || 0) - (b.priority_id || 0);
    });

    return { priorityCols: cols, priorityById: byId };
  }, [priorityRows]);

  const updateEnquiryPriority = useCallback(
    async (enquiry_id, priority_id_or_null) => {
      const prev = enquiries;
      setEnquiries((list) =>
        (list || []).map((x) =>
          String(x.id) === String(enquiry_id)
            ? { ...x, priority_id: priority_id_or_null }
            : x,
        ),
      );

      setSaving(true);
      setBoardErr("");

      try {
        const res = await axios.post(`${API}/setEnquiryPriority`, {
          enquiry_id,
          priority_id: priority_id_or_null,
        });

        if (!res.data?.success) {
          throw new Error(res.data?.message || "Failed to update priority.");
        }
        setNotice("Enquiry moved.");
      } catch (e) {
        console.error(e);
        setEnquiries(prev);
        setBoardErr(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to update priority.",
        );
      } finally {
        setSaving(false);
      }
    },
    [enquiries],
  );

  const moveEnquiry = (enquiryId, targetKey) => {
    if (targetKey === UNASSIGNED) {
      updateEnquiryPriority(enquiryId, null);
      return;
    }

    const pid = Number(targetKey);
    if (pid) updateEnquiryPriority(enquiryId, pid);
  };

  const onDropTo = (targetKey) => {
    if (!dragId) return;
    moveEnquiry(dragId, targetKey);
    setDragId(null);
  };

  const q = safeText(query).trim().toLowerCase();
  const visibleEnquiries = useMemo(() => {
    if (!q) return starredEnquiries;
    return starredEnquiries.filter((e) =>
      [e.name, e.email, e.phone_number, e.country, e.source, e.status]
        .map((v) => safeText(v).toLowerCase())
        .some((v) => v.includes(q)),
    );
  }, [starredEnquiries, q]);

  const columns = useMemo(() => {
    const col = { [UNASSIGNED]: [] };
    priorityCols.forEach((p) => (col[String(p.priority_id)] = []));

    (visibleEnquiries || []).forEach((enq) => {
      const pid = enq.priority_id == null ? null : Number(enq.priority_id);

      if (pid && col[String(pid)]) col[String(pid)].push(enq);
      else col[UNASSIGNED].push(enq);
    });

    const sorter = (a, b) => {
      const da = new Date(a.submission_date || 0).getTime();
      const db = new Date(b.submission_date || 0).getTime();
      return db - da;
    };

    Object.keys(col).forEach((k) => col[k].sort(sorter));
    return col;
  }, [visibleEnquiries, priorityCols]);

  const moveOptions = useMemo(
    () => [
      { key: UNASSIGNED, title: "Enquiries" },
      ...priorityCols.map((p) => ({ key: String(p.priority_id), title: p.priority })),
    ],
    [priorityCols],
  );

  const openCreate = () => {
    setCreateErr("");
    setForm({ priority: "", description: "" });
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (saving) return;
    setShowCreate(false);
  };

  const saveCreate = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const priority = safeText(form.priority).trim();
    const description = safeText(form.description).trim();

    if (!priority) return setCreateErr("Please enter Priority.");
    if (!description) return setCreateErr("Please enter Description.");

    setCreateErr("");
    setSaving(true);

    try {
      const res = await axios.post(`${API}/savePriority`, {
        priority,
        description,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to save priority.");
      }

      setShowCreate(false);
      setNotice(`Column "${priority}" created.`);
      await fetchPriority();
    } catch (err) {
      console.error(err);
      setCreateErr(
        err?.response?.data?.message || err?.message || "Failed to save.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (priority_id) => {
    const meta = priorityById[Number(priority_id)];
    if (!meta?.priority_id) return;

    setDeleteErr("");
    setDeleteTarget({ priority_id: meta.priority_id, priority: meta.priority });
    setShowDelete(true);
  };

  const closeDelete = () => {
    if (saving) return;
    setShowDelete(false);
    setDeleteTarget(null);
    setDeleteErr("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.priority_id) return;

    setDeleteErr("");
    setSaving(true);

    try {
      const res = await axios.post(`${API}/deletePriority`, {
        id: deleteTarget.priority_id,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to delete priority.");
      }

      const removed = deleteTarget.priority;
      closeDelete();
      setNotice(`Column "${removed}" deleted.`);

      await fetchPriority();

      await fetchEnquiries();
    } catch (err) {
      console.error(err);
      setDeleteErr(
        err?.response?.data?.message || err?.message || "Failed to delete.",
      );
    } finally {
      setSaving(false);
    }
  };

  const unassignedCount = starredEnquiries.filter(
    (e) => !(e.priority_id != null && priorityById[Number(e.priority_id)]),
  ).length;

  return (
    <Container fluid className={`min-vh-100 p-3 p-md-4 kbp-page ${isDark ? "dark-theme" : "light-theme"}`}>
      <NavMenu />

      <div className="kbp-hero kbp-fade">
        <div className="kbp-hero-text">
          <span className="kbp-hero-chip"><MdViewKanban /> Priority board</span>
          <h1 className="kbp-hero-title">Pipeline</h1>
          <p className="kbp-hero-sub">
            Drag starred enquiries between columns to track where each one stands.
          </p>
          <div className="kbp-hero-stats">
            <span className="kbp-hero-stat"><b>{starredEnquiries.length}</b> starred</span>
            <span className="kbp-hero-stat"><b>{unassignedCount}</b> unassigned</span>
            <span className="kbp-hero-stat"><b>{priorityCols.length}</b> priority columns</span>
          </div>
        </div>

        <div className="kbp-hero-actions">
          <Button className="kbp-hero-btn" onClick={fetchAll} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-2" /> : <IoMdRefresh className="me-2" />}
            Refresh
          </Button>
          <Button className="kbp-hero-btn" onClick={openCreate} disabled={saving}>
            <MdAdd className="me-1" /> New Priority Column
          </Button>
        </div>
      </div>

      <div className="kbp-toolbar kbp-fade" style={{ "--d": "60ms" }}>
        <div className="kbp-search">
          <MdSearch className="kbp-search-icon" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search starred enquiries..."
            aria-label="Search starred enquiries"
            className="kbp-search-input"
          />
          {query && (
            <button type="button" className="kbp-search-clear" onClick={() => setQuery("")} aria-label="Clear search">
              <IoClose />
            </button>
          )}
        </div>
        <span className="kbp-toolbar-note">
          {q
            ? `${visibleEnquiries.length} of ${starredEnquiries.length} starred enquiries match`
            : "Tip: you can also use the move menu on each card."}
        </span>
      </div>

      {boardErr ? (
        <div className="kbp-error" role="alert">
          <MdErrorOutline aria-hidden="true" />
          <span>{boardErr}</span>
          <button type="button" onClick={() => setBoardErr("")} aria-label="Dismiss error"><IoClose /></button>
        </div>
      ) : null}

      {loading && enquiries.length === 0 && priorityRows.length === 0 ? (
        <div className="kbp-board" aria-busy="true" aria-label="Loading pipeline">
          {[0, 1, 2, 3].map((i) => (
            <div className="kbp-col kbp-col-skel" key={i}>
              <span className="kbp-skel" style={{ height: 22, width: "55%" }} />
              <span className="kbp-skel" style={{ height: 120 }} />
              <span className="kbp-skel" style={{ height: 120 }} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {starredEnquiries.length === 0 && (
            <div className="kbp-banner">
              <span className="kbp-banner-icon"><FaRegStar /></span>
              <div>
                <div className="kbp-banner-title">No starred enquiries yet</div>
                <div className="kbp-banner-sub">
                  Star an enquiry on the Enquiries page and it will show up here, ready to sort into columns.
                </div>
              </div>
            </div>
          )}

          <div className="kbp-board">
            <Column
              title="Enquiries"
              colKey={UNASSIGNED}
              items={columns[UNASSIGNED] || []}
              accentClass="accent-slate"
              deletable={false}
              saving={saving}
              dragId={dragId}
              setDragId={setDragId}
              onDropTo={onDropTo}
              onMove={moveEnquiry}
              moveOptions={moveOptions}
              filtering={!!q}
            />

            {priorityCols.map((p, idx) => (
              <Column
                key={p.priority_id}
                title={p.priority}
                colKey={String(p.priority_id)}
                items={columns[String(p.priority_id)] || []}
                accentClass={accentForIndex(idx)}
                deletable={true}
                onDelete={() => openDelete(p.priority_id)}
                saving={saving}
                dragId={dragId}
                setDragId={setDragId}
                onDropTo={onDropTo}
                onMove={moveEnquiry}
                moveOptions={moveOptions}
                filtering={!!q}
              />
            ))}
          </div>
        </>
      )}

      <Modal show={showCreate} onHide={closeCreate} centered className={isDark ? "kbp-modal-dark" : ""}>
        <Form onSubmit={saveCreate} noValidate>
          <Modal.Header closeButton={!saving} className="kbp-modal-head">
            <Modal.Title className="kbp-modal-title">Create priority column</Modal.Title>
          </Modal.Header>

          <Modal.Body className="kbp-modal-body">
            <Form.Group className="mb-3" controlId="kbp-field-priority">
              <Form.Label>Priority</Form.Label>
              <Form.Control
                autoFocus
                value={form.priority}
                onChange={(e) => {
                  setForm((p) => ({ ...p, priority: e.target.value }));
                  if (createErr) setCreateErr("");
                }}
                placeholder="e.g. Urgent / Call back / Follow up"
                className="kbp-input"
                disabled={saving}
              />
            </Form.Group>

            <Form.Group controlId="kbp-field-desc">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={form.description}
                onChange={(e) => {
                  setForm((p) => ({ ...p, description: e.target.value }));
                  if (createErr) setCreateErr("");
                }}
                placeholder="Explain what this priority means..."
                className="kbp-input"
                disabled={saving}
              />
            </Form.Group>

            {createErr ? (
              <div className="kbp-inline-error mt-3" role="alert">
                <MdErrorOutline aria-hidden="true" /> {createErr}
              </div>
            ) : null}
          </Modal.Body>

          <Modal.Footer className="kbp-modal-foot">
            <Button type="button" className="kbp-btn kbp-btn-ghost" onClick={closeCreate} disabled={saving}>
              Cancel
            </Button>

            <Button type="submit" className="kbp-btn kbp-btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Create column"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDelete} onHide={closeDelete} centered className={isDark ? "kbp-modal-dark" : ""}>
        <Modal.Header closeButton={!saving} className="kbp-modal-head">
          <Modal.Title className="kbp-modal-title">Delete priority column?</Modal.Title>
        </Modal.Header>

        <Modal.Body className="kbp-modal-body">
          <p className="mb-2">
            <strong>{deleteTarget?.priority}</strong> will be removed from the board.
          </p>
          <p className="kbp-muted mb-0">
            Enquiries in this column may need to be updated in DB depending on your backend.
          </p>

          {deleteErr ? (
            <div className="kbp-inline-error mt-3" role="alert">
              <MdErrorOutline aria-hidden="true" /> {deleteErr}
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer className="kbp-modal-foot">
          <Button className="kbp-btn kbp-btn-ghost" onClick={closeDelete} disabled={saving}>
            Cancel
          </Button>

          <Button className="kbp-btn kbp-btn-danger" onClick={confirmDelete} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {notice && (
        <div className="kbp-toast" role="status">
          <MdCheckCircle aria-hidden="true" />
          <span>{notice}</span>
        </div>
      )}
    </Container>
  );
}
